import React, { useEffect, useState, useRef } from 'react';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { CTAButton } from './CTAButton';
const AUDIO_URL = 'http://iniciadasritual.site/wp-content/uploads/2026/02/Voice-of-freedom.mp3';
export function AudioPreview() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(78); // 1:18 fallback
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audio.currentTime = pct * duration;
  };
  const handleVolumeClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newVol = Math.max(0, Math.min(1, x / rect.width));
    setVolume(newVol);
    setIsMuted(false);
    if (audioRef.current) {
      audioRef.current.volume = newVol;
      audioRef.current.muted = false;
    }
  };
  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  const formatTime = (s: number) => {
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const progress = duration > 0 ? currentTime / duration * 100 : 0;
  return <section className="bg-navy-light py-8 px-4 border-t border-blue-900/50" data-id="element-25">
      <audio ref={audioRef} src={AUDIO_URL} preload="metadata" data-id="element-26" />

      <div className="max-w-md mx-auto" data-id="element-27">
        <div className="bg-white rounded-xl p-6 shadow-xl" data-id="element-28">
          <h3 className="text-navy font-bold text-xl mb-6 font-merriweather" data-id="element-29">
            Listen to a Preview
          </h3>

          <div className="flex items-center gap-4 mb-6" data-id="element-30">
            <button onClick={togglePlay} className="w-14 h-14 rounded-full bg-navy flex items-center justify-center text-white hover:bg-navy-light transition-colors flex-shrink-0 shadow-md" aria-label={isPlaying ? 'Pause preview' : 'Play preview'} data-id="element-31">
              {isPlaying ? <Pause size={24} fill="currentColor" data-id="element-32" /> : <Play size={24} className="ml-1" fill="currentColor" data-id="element-33" />}
            </button>

            <div className="flex-1" data-id="element-34">
              <div className="h-2 bg-gray-200 rounded-full w-full mb-2 relative overflow-hidden cursor-pointer" onClick={handleProgressClick} data-id="element-35">
                <div className="absolute top-0 left-0 h-full bg-navy rounded-full transition-[width] duration-150" style={{
                width: `${progress}%`
              }} data-id="element-36" />
              </div>
              <div className="flex justify-between text-xs text-gray-500 font-mono" data-id="element-37">
                <span data-id="element-38">{formatTime(currentTime)}</span>
                <span data-id="element-39">{formatTime(duration)}</span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 mb-8 text-gray-400" data-id="element-40">
            <button onClick={toggleMute} className="hover:text-navy transition-colors" aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-41">
              {isMuted ? <VolumeX size={18} data-id="element-42" /> : <Volume2 size={18} data-id="element-43" />}
            </button>
            <div className="h-1.5 bg-gray-200 rounded-full w-24 cursor-pointer relative" onClick={handleVolumeClick} data-id="element-44">
              <div className="h-full bg-navy rounded-full" style={{
              width: `${isMuted ? 0 : volume * 100}%`
            }} data-id="element-45" />
            </div>
          </div>

          <CTAButton onClick={() => document.getElementById('offer')?.scrollIntoView({
          behavior: 'smooth'
        })} data-id="element-46">
            YES! I Want This eBook
          </CTAButton>
        </div>
      </div>
    </section>;
}